/**
 * @fileoverview Combine provider organization billing with independently recorded application attempts. Provider/period filters live in the URL; each report retains its own availability, currency, and coverage. A failed source must not erase successful reports or turn unknown spend into zero. `ProviderSpendDashboard.js` supplies billing detail presentation; `AiControlCenter.js` connects feature usage to settings. Provider billed totals and local estimates measure different scopes.
 */
import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import api from '../../api'
import { AppIcon } from '../shared'
import { ProviderSpendDetails } from './ProviderSpendDashboard'
import './AiSpendDashboard.css'
import AiDailyBudget from './AiDailyBudget'

const PROVIDERS = [
    { id: 'openai', name: 'OpenAI' },
    { id: 'anthropic', name: 'Anthropic' },
]
const PERIODS = [7, 14, 30, 60, 90]

 
function money(value, currency = 'usd') {
    if (value === null || value === undefined) return 'Unknown'
    const amount = Number(value)
    const digits = amount !== 0 && Math.abs(amount) < 0.0001 ? 6 : amount !== 0 && Math.abs(amount) < 1 ? 4 : 2
    return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: (currency || 'usd').toUpperCase(),
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(amount)
}

 
function dayLabel(value) {
    return new Date(value + 'T00:00:00Z').toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', timeZone: 'UTC',
    })
}

/**
 * Provider invoices and local instrumented attempts are independent reports. Keep unknown amounts and partial source coverage visible; aggregate only compatible currencies. onFeature navigates to configuration without changing report filters or feature state.
 */
export default function AiSpendDashboard({ features = [], environment, refreshKey = 0, onFeature }) {
    const [params, setParams] = useSearchParams()
    const days = PERIODS.includes(Number(params.get('days'))) ? Number(params.get('days')) : 30
    const provider = PROVIDERS.some(p => p.id === params.get('provider')) ? params.get('provider') : 'all'
    const [state, setState] = useState({ loading: true, reports: {}, usage: null, errors: {} })

    useEffect(  () => {
        let cancelled = false
        const selected = PROVIDERS.filter(p => provider === 'all' || p.id === provider)
        setState({ loading: true, reports: {}, usage: null, errors: {} })
        const requests = selected.map(p => ({
            key: p.id, request: api.get('/admin/' + p.id + '-spend', { params: { days } }),
        }))
        requests.push({ key: 'usage', request: api.get('/admin/ai/usage', { params: { days, provider } }) })
        Promise.allSettled(requests.map(r => r.request)).then(  results => {
            if (cancelled) return
            const next = { loading: false, reports: {}, usage: null, errors: {} }
            results.forEach((result, index) => {
                const key = requests[index].key
                if (result.status === 'rejected') {
                    next.errors[key] = result.reason?.response?.data?.error || 'Could not load this report.'
                } else if (key === 'usage') {
                    next.usage = result.value.data
                } else {
                    next.reports[key] = result.value.data
                }
            })
            setState(next)
        })
        return () => { cancelled = true }
    }, [days, provider, refreshKey])

    const selected = PROVIDERS.filter(p => provider === 'all' || p.id === provider)
    const billed = selected.filter(p => state.reports[p.id]?.costs?.available)
    const currencies = [...new Set(billed.map(p => (state.reports[p.id].costs.currency || 'usd').toLowerCase()))]
    const complete = billed.length === selected.length
    const billedTotal = billed.reduce((sum, p) => sum + Number(state.reports[p.id].costs.total_amount || 0), 0)
    const rows = state.usage?.rows || []
    const calls = rows.reduce((sum, r) => sum + r.calls, 0)
    const priced = rows.reduce((sum, r) => sum + r.priced_calls, 0)
    const estimate = priced ? rows.reduce((sum, r) => sum + (r.estimated_cost || 0), 0) : null
    const failures = rows.reduce((sum, r) => sum + r.calls - r.succeeded, 0)
    const names = Object.fromEntries(features.map(f => [f.key, f.name]))
    const scope = state.usage?.environment || environment || 'Current environment'
    const updateFilter =   (key, value) => {
        const next = new URLSearchParams(params)
        next.set(key, value)
        setParams(next)
    }

    return <div className="ai-spend">
        <div className="ai-spend__toolbar">
            <h3>Spend & usage</h3>
            <div className="ai-spend__filters">
                <label>Period<select value={days} onChange={e => updateFilter('days', e.target.value)}>
                    {PERIODS.map(d => <option key={d} value={d}>Last {d} days</option>)}
                </select></label>
                <label>Provider<select value={provider} onChange={e => updateFilter('provider', e.target.value)}>
                    <option value="all">All providers</option>
                    {PROVIDERS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select></label>
            </div>
        </div>
        {state.loading ? <p className="ai-spend__loading" role="status">Loading spending and usage…</p> : <>
            {selected.map(p => {
                const costs = state.reports[p.id]?.costs
                const warning = state.errors[p.id] || (costs?.available === false && (costs.warning || 'Billing unavailable.'))
                return warning ? <div key={p.id} role="status" className="ai-center__notice is-warning">
                    <AppIcon icon="pi pi-exclamation-triangle" /><span><strong>{p.name} billing unavailable.</strong> {warning}</span>
                </div> : null
            })}
            {state.errors.usage && <div role="alert" className="ai-center__notice is-error">
                Feature usage unavailable. {state.errors.usage}
            </div>}

            <AiDailyBudget refreshKey={refreshKey} />
            <dl className="ai-spend__metrics" aria-label="AI spend summary">
                <div className="ai-spend__metric ai-spend__metric--billed">
                    <dt>Billed spend {!complete && billed.length > 0 && <span className="ai-spend__badge">Partial</span>}</dt>
                    <dd>{!billed.length ? 'Unavailable' : currencies.length > 1 ? 'Multiple currencies' : money(billedTotal, currencies[0])}</dd>
                    <small>Organization billing · all environments</small>
                    <ul className="ai-spend__provider-totals">{selected.map(p => <li key={p.id}>
                        <span><i className={'ai-spend__swatch is-' + p.id} />{p.name}</span>
                        <strong>{state.reports[p.id]?.costs?.available ? money(state.reports[p.id].costs.total_amount, state.reports[p.id].costs.currency) : 'Unavailable'}</strong>
                    </li>)}</ul>
                </div>
                <div className="ai-spend__metric">
                    <dt>Recorded provider calls</dt>
                    <dd>{state.usage ? calls.toLocaleString() : 'Unavailable'}</dd>
                    <small className="ai-spend__scope">{scope}</small>
                    <p>{state.usage ? failures.toLocaleString() + ' failed' : 'Usage report unavailable'}</p>
                </div>
                <div className="ai-spend__metric">
                    <dt>Estimated token cost {priced > 0 && priced < calls && <span className="ai-spend__badge">Partial</span>}</dt>
                    <dd>{state.usage ? money(estimate) : 'Unavailable'}</dd>
                    <small>{state.usage ? priced.toLocaleString() + ' of ' + calls.toLocaleString() + ' calls priced' : 'Usage report unavailable'}</small>
                    <p>Excludes tool fees and unpriced calls</p>
                </div>
            </dl>

            {billed.length > 0 && currencies.length === 1 &&
                <SpendTrend providers={billed} reports={state.reports} currency={currencies[0]} partial={!complete} />}

            <section className="ai-spend__section" aria-labelledby="ai-feature-usage">
                <header><h3 id="ai-feature-usage">Usage by feature</h3><span className="ai-spend__scope">{scope}</span></header>
                <p className="ai-spend__caption">Recorded provider attempts, including retries and example checks. Billing and local usage have different scopes.</p>
                {rows.length ? <div className="ai-center__table"><table>
                    <thead><tr><th>Feature</th><th>Provider</th><th>Provider calls</th><th>Failed</th><th>Avg. time</th><th>Estimated token cost</th></tr></thead>
                    <tbody>{rows.map(row => <tr key={[row.task_key, row.kind, row.provider].join(':')}>
                        <td>{names[row.task_key] && onFeature
                            ? <button type="button" className="ai-spend__feature-link" onClick={() => onFeature(row.task_key)}>{names[row.task_key]}</button>
                            : names[row.task_key] || row.task_key}
                            {row.kind === 'example' && <small>Example checks</small>}</td>
                        <td>{PROVIDERS.find(p => p.id === row.provider)?.name || 'Unknown'}</td>
                        <td>{row.calls.toLocaleString()}</td><td>{row.calls - row.succeeded}</td>
                        <td>{(row.latency_ms / 1000).toFixed(1)} s</td>
                        <td>{money(row.estimated_cost)}<small>{row.priced_calls}/{row.calls} calls priced</small></td>
                    </tr>)}</tbody>
                </table></div> : <p className="ai-center__empty">{state.usage ? 'No provider calls recorded for this period.' : 'Feature usage is unavailable.'}</p>}
            </section>

            <div className="ai-spend__details">
                {selected.filter(p => state.reports[p.id]).map(p => <details className="ai-spend__detail" key={p.id}>
                    <summary><AppIcon icon="pi pi-file" /><span>{p.name} billing details</span><span className="ai-spend__detail-hint">Invoice, tokens & workflow runs</span></summary>
                    <div className="ai-spend__detail-body">
                        <p className="ai-spend__caption">Workflow runs below cover legacy intake tracking{p.id === 'openai' ? ' and document edits' : ''}. A run can make several provider calls.</p>
                        {state.reports[p.id].local_activity?.available === false && <p role="status">{state.reports[p.id].local_activity.warning || 'Workflow activity unavailable.'}</p>}
                        {state.reports[p.id].usage?.available === false && <p role="status">{state.reports[p.id].usage.warning || 'Provider token usage unavailable.'}</p>}
                        <ProviderSpendDetails dashboard={state.reports[p.id]} providerName={p.name}
                            lineItemsDescription="Organization invoice categories."
                            sourceDescription="Tracked workflow runs in this environment." />
                    </div>
                </details>)}
            </div>
        </>}
    </div>
}

 
function SpendTrend({ providers, reports, currency, partial }) {
    const byDay = {}
    providers.forEach(p => (reports[p.id].costs.daily || []).forEach(row => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(row.date)) return
        if (!byDay[row.date]) byDay[row.date] = { date: row.date }
        byDay[row.date][p.id] = (byDay[row.date][p.id] || 0) + Number(row.amount || 0)
    }))
    const daily = Object.values(byDay).sort((a, b) => a.date.localeCompare(b.date))
    if (!daily.length) return null
    const positive = Math.max(0, ...daily.map(r => providers.reduce((n, p) => n + Math.max(0, r[p.id] || 0), 0)))
    const negative = Math.max(0, ...daily.map(r => providers.reduce((n, p) => n + Math.max(0, -(r[p.id] || 0)), 0)))
    const scale = 115 / (positive + negative || 1)
    const baseline = positive + negative ? 10 + positive * scale : 125
    const step = 760 / daily.length
    return <section className="ai-spend__section ai-spend__trend" aria-labelledby="ai-daily-billing">
        <header><h3 id="ai-daily-billing">Daily billed spend {partial && <span className="ai-spend__badge">Partial</span>}</h3>
            <div className="ai-spend__legend">{providers.map(p => <span key={p.id}><i className={'ai-spend__swatch is-' + p.id} />{p.name}</span>)}</div>
        </header>
        <svg viewBox="0 0 760 140" preserveAspectRatio="none" role="img" aria-label="Daily billed spend by provider">
            <line x1="0" x2="760" y1={baseline} y2={baseline} className="ai-spend__axis" />
            {daily.map((row, index) => {
                let up = baseline, down = baseline
                return <g key={row.date}><title>{dayLabel(row.date) + ': ' + providers.map(p => p.name + ' ' + money(row[p.id] || 0, currency)).join(', ')}</title>
                    {providers.map(  p => {
                        const value = row[p.id] || 0
                        const height = Math.abs(value) * scale
                        const y = value >= 0 ? (up -= height) : down
                        if (value < 0) down += height
                        return <rect key={p.id} className={'ai-spend__bar is-' + p.id} x={index * step + 2} y={y}
                            width={Math.max(1, step - 4)} height={height} rx="1" />
                    })}
                </g>
            })}
        </svg>
        <div className="ai-spend__dates"><span>{dayLabel(daily[0].date)}</span><span>{dayLabel(daily[daily.length - 1].date)}</span></div>
        <details className="ai-spend__daily-table"><summary>Daily amounts</summary><div className="ai-center__table"><table>
            <thead><tr><th>Date (UTC)</th>{providers.map(p => <th key={p.id}>{p.name}</th>)}</tr></thead>
            <tbody>{daily.map(row => <tr key={row.date}><td>{dayLabel(row.date)}</td>
                {providers.map(p => <td key={p.id}>{money(row[p.id] || 0, currency)}</td>)}</tr>)}</tbody>
        </table></div></details>
    </section>
}
